import { ObservableSet } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";

import { DateTime } from "@bps/date-time";
import { IDetailsListProps, Text } from "@bps/fluent-ui";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import { XeroFinancialSyncStatus } from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";

import { useIntegrationScreenContext } from "../context/IntegrationScreenContext.ts";
import { XeroSyncHistoryItemBadge } from "./Xero/XeroSyncHistoryItemBadge.tsx";
import { XeroSyncHistoryItemIcons } from "./Xero/XeroSyncHistoryItemIcons.tsx";
import { getOmniLinkUrl } from "./Xero/XeroSyncHistoryListItem.utils.ts";
import {
  XeroFinancialSyncOperationListItem,
  XeroSyncHistoryListRow
} from "./Xero/XeroSyncHistoryListRow.tsx";

interface XeroSyncHistoryListProps {
  refreshKey: string;
  showAll: boolean;
}

export const XeroSyncHistoryList = observer(
  ({ refreshKey, showAll }: XeroSyncHistoryListProps) => {
    const { customIntegrationXero } = useStores();
    const { xeroTenantId } = useIntegrationScreenContext();

    const [openRows] = useState<ObservableSet>(new ObservableSet());

    const getItems = useCallback(
      (query: PagingOptions) => {
        return customIntegrationXero.getFinancialSyncSource({
          ...query,
          targetTenantId: xeroTenantId,
          statuses: showAll
            ? undefined
            : [
                XeroFinancialSyncStatus.Warnings,
                XeroFinancialSyncStatus.Errors,
                XeroFinancialSyncStatus.InternalFailure
              ]
        });
      },
      [customIntegrationXero, showAll, xeroTenantId]
    );

    const onRenderRow: IDetailsListProps["onRenderRow"] = (
      props,
      defaultRender
    ) => {
      if (!props || !defaultRender) {
        return null;
      }

      return (
        <XeroSyncHistoryListRow
          props={props}
          defaultRender={defaultRender}
          openRows={openRows}
        />
      );
    };

    return (
      <InfiniteScrollList
        refreshKey={refreshKey}
        detailsListStyles={{ root: { overflow: "hidden" } }}
        getItems={getItems}
        onRenderRow={onRenderRow}
        columns={[
          {
            name: "Updated",
            key: "date",
            minWidth: 100,
            maxWidth: 100,
            onRender: (operation: XeroFinancialSyncOperationListItem) => {
              const date =
                operation.changeLog.updatedDate ||
                operation.changeLog.createdDate;
              return <Text>{DateTime.fromISO(date).toDayDefaultFormat()}</Text>;
            }
          },
          {
            name: "Operation",
            key: "operation",
            minWidth: 250,
            maxWidth: 250,
            onRender: (operation: XeroFinancialSyncOperationListItem) => {
              const omniUrl = getOmniLinkUrl(operation);
              const urlText = operation.operationType;
              if (omniUrl) {
                return <Navigate to={omniUrl}>{urlText}</Navigate>;
              }
              return <Text>{urlText}</Text>;
            }
          },
          {
            name: "",
            key: "icons",
            minWidth: 50,
            maxWidth: 50,
            onRender: (operation: XeroFinancialSyncOperationListItem) => {
              return (
                <XeroSyncHistoryItemIcons
                  openRows={openRows}
                  operation={operation}
                />
              );
            }
          },
          {
            name: "Status",
            key: "status",
            minWidth: 100,
            maxWidth: 100,
            onRender: (operation: XeroFinancialSyncOperationListItem) => {
              return <XeroSyncHistoryItemBadge operation={operation} />;
            }
          },
          {
            name: "Status details",
            key: "details",
            minWidth: 200,
            maxWidth: 300,
            onRender: (operation: XeroFinancialSyncOperationListItem) => {
              if (operation.statusDetails?.length) {
                return operation.statusDetails.map(({ message }) => (
                  <Text key={message}>{message}</Text>
                ));
              }

              return null;
            }
          }
        ]}
      />
    );
  }
);
